import ops from "../../styles/opsConsole.module.css";
import css from "./Vendors.module.css";
import { STATUS_LABEL, STATUS_TONE, normStatus } from "./vendorApi";

/**
 * Small pieces shared by the vendor list and the vendor record.
 *
 * The icons are local rather than imported from opsBits because these render
 * at 11–12px inside badges and chips, where that file's 19px KPI sizing does
 * not apply. Same stroke language, same palette — inherited via currentColor
 * from the shared severity classes, so nothing here re-invents a state colour.
 */

const svg = (children, extra = {}) => (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    {...extra}
    {...props}
  >
    {children}
  </svg>
);

export const Check = svg(<path d="M20 6 9 17l-5-5" />);
export const Clock = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3.5 2" />
  </>
);
export const QMark = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M9.4 9.2a2.7 2.7 0 0 1 5.2.9c0 1.8-2.6 2.4-2.6 4M12 17.4h.01" />
  </>
);
export const Cross = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="m15 9-6 6M9 9l6 6" />
  </>
);
export const Search = svg(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.6-3.6" />
  </>
);
export const ArrowUp = svg(<path d="M12 19V5M6 11l6-6 6 6" />);
export const ArrowDown = svg(<path d="M12 5v14M18 13l-6 6-6-6" />);
export const Sortable = svg(<path d="M8 9.5 12 5l4 4.5M8 14.5l4 4.5 4-4.5" />, {
  strokeWidth: 2,
});
export const External = svg(
  <>
    <path d="M14 4h6v6" />
    <path d="M20 4 11 13" />
    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
  </>
);
export const Mail = svg(
  <>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m3.5 7 8.5 6 8.5-6" />
  </>
);
export const Phone = svg(
  <path d="M5 3h3.5l1.8 4.3-2.2 1.3a12.5 12.5 0 0 0 5.3 5.3l1.3-2.2L19 13.5V17a2 2 0 0 1-2.2 2A15.8 15.8 0 0 1 3 5.2 2 2 0 0 1 5 3z" />
);
export const Globe = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M3.2 9h17.6M3.2 15h17.6M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
  </>
);
export const Cal = svg(
  <>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </>
);
export const Copy = svg(
  <>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </>
);
export const FileOff = svg(
  <>
    <path d="M14 3v5h5" />
    <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5" />
    <path d="M9 13h6M9 17h4" />
  </>
);
export const Bank = svg(
  <>
    <path d="M3 10 12 4l9 6" />
    <path d="M5 10v9M19 10v9M9.5 10v9M14.5 10v9M3 20h18" />
  </>
);
export const Receipt = svg(
  <>
    <path d="M5 3h14v18l-2.3-1.6L14.4 21l-2.4-1.6L9.6 21l-2.3-1.6L5 21z" />
    <path d="M9 8h6M9 12h6" />
  </>
);
export const Alert = svg(
  <>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
    <path d="M12 9v4M12 17h.01" />
  </>
);

const STATUS_ICON = {
  verified: Check,
  pending: Clock,
  unverified: QMark,
  rejected: Cross,
};

/* ────────────────────────────────────────────────────────────────────────
   Verification badge

   Colour is never the only carrier: every badge shows an icon whose SHAPE
   differs per state (tick / clock / question / cross) and the word itself.
   Read in greyscale it still reads.
   ──────────────────────────────────────────────────────────────────────── */
export function VerificationBadge({ status, small }) {
  const s = normStatus(status);
  const Ico = STATUS_ICON[s];
  return (
    <span
      className={`${ops.badge} ${ops[STATUS_TONE[s]]} ${css.stateBadge} ${
        small ? css.stateSm : ""
      }`}
    >
      <Ico />
      {STATUS_LABEL[s]}
    </span>
  );
}

export function StatusIcon({ status }) {
  const Ico = STATUS_ICON[normStatus(status)];
  return (
    <span className={css.chipIco}>
      <Ico />
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Sortable column header

   Enterprise behaviour, nothing clever: click to sort, click the active
   column again to reverse, one sort at a time. The direction is announced
   three ways — aria-sort for assistive tech, the arrow glyph, and the
   header's own emphasis — so it never depends on noticing a tint.
   ──────────────────────────────────────────────────────────────────────── */
export function SortTh({ id, label, sort, dir, onSort, numeric, hint }) {
  const active = sort === id;
  const Arrow = !active ? Sortable : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
      className={`${numeric ? ops.num : ""} ${numeric ? css.thNum : ""} ${
        active ? css.thOn : ""
      }`}
    >
      <button
        type="button"
        className={css.sortBtn}
        onClick={() => onSort(id)}
        title={
          hint ||
          (active
            ? `Sorted ${dir === "asc" ? "ascending" : "descending"} — click to reverse`
            : `Sort by ${label}`)
        }
      >
        {label}
        <span className={css.sortIco}>
          <Arrow />
        </span>
        <span className={css.srOnly}>
          {active
            ? `, sorted ${dir === "asc" ? "ascending" : "descending"}, activate to reverse`
            : ", activate to sort"}
        </span>
      </button>
    </th>
  );
}

/** Plain header, for columns nothing sorts by. */
export function Th({ label, numeric }) {
  return (
    <th scope="col" className={numeric ? ops.num : undefined}>
      {label}
    </th>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   States every async region owes the operator
   ──────────────────────────────────────────────────────────────────────── */

export function SkeletonRows({ cols, rows = 5 }) {
  return Array.from({ length: rows }, (_, r) => (
    <tr key={r}>
      {Array.from({ length: cols }, (__, c) => (
        <td key={c}>
          <span
            className={css.skel}
            style={{ width: c === 0 ? "62%" : `${38 + ((c * 13) % 30)}%` }}
          />
        </td>
      ))}
    </tr>
  ));
}

export function ErrorCard({ message, onRetry }) {
  return (
    <div className={`${ops.card} ${ops.askBox}`}>
      <div className={css.errBox}>
        <span className={css.sectionGapIco} style={{ background: "var(--danger-soft)", color: "var(--danger)" }}>
          <Alert />
        </span>
        <div>
          <b>This did not load</b>
          <p>{message}</p>
          {onRetry && (
            <button type="button" className={`${ops.btn} ${css.btnPlain}`} onClick={onRetry}>
              Try again
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * A value that may legitimately be missing. Most vendors have never filled
 * their tax or bank record in, so "nothing here" has to look like a fact
 * about the vendor rather than a fault in the page.
 */
export function Value({ children, mono, empty = "Not provided" }) {
  const has = children !== null && children !== undefined && children !== "";
  if (!has) return <span className={css.gapVal}>{empty}</span>;
  return <span className={mono ? ops.mono : undefined}>{children}</span>;
}
